import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useGateValue } from '@statsig/react-bindings';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Icon,
  Flex,
  Form,
  toast,
  Checkbox,
  Label,
  Separator,
  Text,
  Spinner,
  Alert,
  AlertDescription,
} from '@kognitos/lattice';
import { IntegrationAssistantBanner } from './IntegrationAssistantBanner';
import { IntegrationAssistantPanel } from './IntegrationAssistantPanel';
import {
  ConnectionDiagnosticsDetail,
  ConnectionDiagnosticsPopover,
  hasConnectionFailure,
  isHealthCheckSettled,
  shouldShowHealthDetails,
} from './ConnectionDiagnostics';
import { ConnectionStatusTag } from './ConnectionStatusTag';
import { useConnectionDiagnosticsInsight } from '../hooks/use-connection-diagnostics-insight';
import {
  Bdkv2CredentialType,
  Bdkv2OAuthFlow,
  type Bdkv2BookDescriptor,
  type V2BookConnectionResponse,
  type V2BookConnectionDescriptor,
  type Bdkv2Value,
} from '@/generated/hooks/bdk/models';
import {
  getRetrieveAllWorkspaceBookConnectionsQueryKey,
  useRetrieveAllWorkspaceBookConnections,
} from '@/generated/hooks/bdk/v2/services/library';
import { useListRemoteConnections } from '@/generated/hooks/bifrost/v1/api';
import {
  ConfigMapStep,
  CustomCredentialsStep,
  EnvironmentStatus,
  NoAuthConnectionStep,
  OAuthClientCredentialsStep,
  OAuthSignInButton,
  RemoteConnectionStep,
  ReuseTestConnectionAlert,
  SelectAuthenticationMethodStep,
  useReuseTestConnectionAlert,
  useWaitForConnectionToBeReady,
} from '..';
import {
  ConnectionMethodStep,
  type ConnectionMethodStepData,
} from './ConnectionMethodStep';
import {
  type AddConnectionWizardData,
  type AuthenticationMethodStepData,
  type AuthenticationCredentialsStepData,
  type ConfigMapFormData,
  ConnectionType,
  EnvironmentType,
  createAuthenticationCredentialsSchema,
  createConfigMapSchema,
} from '../schemas';
import {
  buildCredentialsFormDefaultValues,
  buildConfigMapFormDefaults,
  classifyDuplicateCredentialsError,
  getAuthMethodId,
  getCredentialsForAuthMethod,
  getDuplicateConnectionError,
  hasConfigMap,
  hasSharedConnection,
  isCredentialsRequiredError,
  mapConfigMapToApiFormat,
  normalizeAuthenticationMethods,
  getKeyValueCredentialIds,
  promoteDraftToProductionConnection,
  resolveConnectionLabel,
  shouldShowDirectOAuthButton,
} from '../utils';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores/user-preferences.store';
import { captureException } from '@/modules/sentry';
import {
  createWithAvailableName,
  defaultCredentialDisplayName,
  useAbandonOAuthSignIn,
  useCompatibleCredentials,
  useCreateCredential,
  useCreateOAuthCredential,
  writePendingOAuthConnection,
  type ICredentialCreateFormValues,
  type IOAuthConnectionResume,
} from '@/modules/credentials';
import { usePermissions } from '@/shared/permissions';
import { FEATURE_FLAGS } from '@/shared/types/features';
import { FilterBuilder, validateOAuthRedirectUrl } from '@/shared/utils';
import { hasInvalidKeyValueCredentials } from './CredentialKeyValueInput';
import {
  EmptyCredentialValuesConfirmDialog,
  useEmptyCredentialValuesConfirmation,
} from './EmptyCredentialValuesConfirmDialog';
import { ManagedCredentialStep } from './ManagedCredentialStep';
import { OAuthAppPicker } from './OAuthAppPicker';
import {
  useManagedCredential,
  type IResolvedCredential,
} from '../hooks/use-managed-credential';
import {
  oauthArgumentsToFill,
  useSharedAuthentications,
  type TSharedAuthEntry,
} from '../hooks/use-shared-authentications';
import { getOAuthProviderConfig } from './OAuthSignInButton';
import { useRemoteSecretConnectionActions } from '../hooks/use-remote-secret-connection-actions';

export interface IAddConnectionDialogProps {
  isOpen: boolean;
  onCancel: () => void;
  showSuccessToast?: boolean;
  onSuccess: (
    draftConnection?: V2BookConnectionDescriptor,
    publishedConnection?: V2BookConnectionDescriptor,
  ) => void;
  onOAuthClick?: (
    authMethodId: string,
    connectionName: string,
    credentials: Record<string, string>,
    labels: string[],
    configMap?: Record<string, Bdkv2Value>,
  ) => Promise<void>;
  integration: Bdkv2BookDescriptor;
  requireProductionCredentials?: boolean;
  createConnection: (
    integration: Bdkv2BookDescriptor,
    connectionName: string,
    authMethodId: string,
    credentials: Record<string, string>,
    labels: string[],
    oauthFlow?: Bdkv2OAuthFlow,
    configMap?: Record<string, Bdkv2Value>,
    remoteConnectionId?: string,
  ) => Promise<V2BookConnectionResponse>;
  updateConnection: (
    connection: V2BookConnectionDescriptor,
    credentials: Record<string, string>,
    integration: Bdkv2BookDescriptor,
    configMap?: Record<string, Bdkv2Value>,
  ) => Promise<V2BookConnectionResponse>;
  /**
   * The host reopens the wizard after an OAuth sign-in redirect (see
   * `useManagedOAuthReturn`). Otherwise the connection is created on return.
   */
  supportsOAuthResume?: boolean;
  /**
   * Tags a sign-in with the screen that started it, so only that screen's
   * wizard reopens when several share a page.
   */
  oauthOrigin?: string;
  /**
   * Called right before a managed-credential sign-in leaves the page, e.g. so
   * a trigger dialog can reopen on return.
   */
  onManagedSignInRedirect?: () => void;
  /**
   * Reopen the wizard after the browser returns from an OAuth provider: on the
   * credential step, with the credential just authorized selected (or the
   * sign-in error shown). Read on mount only.
   */
  oauthResume?: IOAuthConnectionResume | null;
}

export function AddConnectionDialog({
  isOpen,
  onCancel,
  showSuccessToast = true,
  onSuccess,
  onOAuthClick,
  integration,
  requireProductionCredentials = false,
  createConnection,
  updateConnection,
  supportsOAuthResume = false,
  oauthOrigin,
  onManagedSignInRedirect,
  oauthResume,
}: IAddConnectionDialogProps) {
  const { t: tIntegrations } = useTranslation('integrations');
  const { t: tCommon } = useTranslation('common');
  const { t: tCredentials } = useTranslation('credentials');
  const queryClient = useQueryClient();
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const hasNoAuth =
    !integration.authentications || integration.authentications.length === 0;

  // Tracks whether the user has moved past the connection method step, rather
  // than whether that step is currently on screen. The latter is derived
  // below, so it can react to data that arrives after mount — see
  // `showConnectionMethodStep`.
  const [
    hasAdvancedPastConnectionMethodStep,
    setHasAdvancedPastConnectionMethodStep,
  ] = useState(!!oauthResume);
  const [connectionMethodStepData, setConnectionMethodStepData] =
    useState<ConnectionMethodStepData>({
      connectionName: '',
      useCredentials: false,
    });

  const {
    data: integrationConnectionsData,
    isLoading: isLoadingIntegrationConnections,
  } = useRetrieveAllWorkspaceBookConnections(organizationId, workspaceId, {
    filter: FilterBuilder.create()
      .has('book_name', integration.name || '')
      .build(),
  });

  const connections = integrationConnectionsData?.data?.book_connections;

  // Offer the with/without-credentials choice only when connecting without
  // credentials is actually a distinct outcome. Once a shared connection
  // exists it already *is* the credential-free connection for this book, so
  // choosing "without credentials" would just clone it; the user opening this
  // wizard wants to authenticate as themselves. Skip straight to the auth
  // methods in that case.
  //
  // Derived rather than stored: `connections` arrives asynchronously, and a
  // value seeded at mount would still show the choice after the shared
  // connection loaded.
  // `FilterBuilder.has` is a contains match, so the query can return
  // connections for a book whose name merely contains this one's. Narrow to an
  // exact match before deciding, the same way IntegrationDetailsContent does.
  const showConnectionMethodStep =
    integration.connection_required === false &&
    !hasNoAuth &&
    !hasSharedConnection(
      connections?.filter(
        connection => connection.book_name === integration.name,
      ),
    );

  const isShowingConnectionMethodStep =
    showConnectionMethodStep && !hasAdvancedPastConnectionMethodStep;

  // Surfaces a connection error to the user and reports genuine failures to
  // Sentry. Duplicate errors are expected user input (the user tried to create
  // a connection whose credentials or name already exist), so each gets a
  // dedicated toast but is NOT reported.
  const showConnectionError = useCallback(
    (
      error: unknown,
      fallbackKey: string = 'integrations.addConnection.error',
      action: string = 'createConnection',
    ) => {
      const duplicate = getDuplicateConnectionError(error);
      if (duplicate?.reason === 'credentials') {
        toast.error(
          tIntegrations('integrations.addConnection.duplicateCredentials', {
            connectionName: resolveConnectionLabel(
              connections,
              duplicate.existingId,
            ),
          }),
        );
        return;
      }
      if (duplicate?.reason === 'name') {
        toast.error(tIntegrations('integrations.addConnection.duplicateName'));
        return;
      }

      // A credential-less add refused because a shared connection already
      // covers this book. Expected user input like the duplicates above, so
      // it gets its own message and is not reported to Sentry.
      if (isCredentialsRequiredError(error)) {
        toast.error(
          tIntegrations('integrations.addConnection.credentialsRequired'),
        );
        return;
      }

      toast.error(tIntegrations(fallbackKey));
      captureException(error as Error, {
        tags: { component: 'AddConnectionDialog', action },
      });
    },
    [tIntegrations, connections],
  );

  const [currentStep, setCurrentStep] = useState(oauthResume ? 2 : 1);
  const [wizardData, setWizardData] = useState<
    Partial<AddConnectionWizardData>
  >(() =>
    oauthResume
      ? {
          connectionName: oauthResume.connectionName,
          authMethodId: oauthResume.authMethodId,
          createSameConnection: true,
        }
      : {},
  );
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSidePanel, setOpenSidePanel] = useState<'assistant' | null>(null);
  const isAssistantOpen = openSidePanel === 'assistant';
  const isAssistantEnabled = useGateValue('enable_integration_assistant');
  const [isConnectionTakingTooLong, setIsConnectionTakingTooLong] =
    useState(false);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const successTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clear pending timers if the dialog unmounts (e.g. navigating away during
  // the healthy beat) so neither fires onSuccess / setState after unmount.
  // eslint-disable-next-line no-restricted-syntax -- reason: timer cleanup on unmount
  useEffect(
    () => () => {
      clearTimeout(loadingTimerRef.current);
      clearTimeout(successTimerRef.current);
    },
    [],
  );

  // The connection whose health we show inline in the dialog while testing:
  // checking → healthy (shown briefly, then close) → failed (kept open with the
  // shifu diagnosis). Only used for discover-capable books when the flag is on.
  const [panelConnection, setPanelConnection] = useState<
    V2BookConnectionDescriptor | undefined
  >();
  const hasSettledCheck =
    !!panelConnection && isHealthCheckSettled(panelConnection.status);
  // Loading, not health state: a timed-out check never settles its status.
  const isCheckingConnection = !!panelConnection && isLoading;
  const diagnosisInsight = useConnectionDiagnosticsInsight({
    connection: panelConnection,
    displayName: integration.display_name || integration.name,
    enabled:
      isAssistantEnabled && hasConnectionFailure(panelConnection?.status),
  });
  const completedDiagnosis = diagnosisInsight.isLoading
    ? undefined
    : diagnosisInsight.insight || undefined;
  const panelStatusTag = (
    <ConnectionStatusTag
      connections={panelConnection ? [panelConnection] : []}
      status={panelConnection?.status?.state || 'BOOK_CONNECTION_STATE_PENDING'}
      style="icon"
    />
  );

  // Kept across resubmits (they update the same connection) so the assistant
  // isn't flipped back to the setup flow, wiping its diagnosis chat.
  const [diagnosedConnectionId, setDiagnosedConnectionId] = useState<string>();
  const failedConnectionId =
    isAssistantEnabled &&
    hasSettledCheck &&
    hasConnectionFailure(panelConnection?.status)
      ? panelConnection?.connection_id
      : undefined;
  if (failedConnectionId && failedConnectionId !== diagnosedConnectionId) {
    setDiagnosedConnectionId(failedConnectionId);
    setOpenSidePanel('assistant');
  }

  const startLoading = () => {
    setIsLoading(true);
    setIsConnectionTakingTooLong(false);
    setPanelConnection(undefined);
    loadingTimerRef.current = setTimeout(
      () => setIsConnectionTakingTooLong(true),
      30_000,
    );
  };

  const stopLoading = () => {
    setIsLoading(false);
    setIsConnectionTakingTooLong(false);
    clearTimeout(loadingTimerRef.current);
  };

  // Track created draft connection to pass to published step's onSuccess
  const [createdDraftConnection, setCreatedDraftConnection] = useState<
    V2BookConnectionDescriptor | undefined
  >();

  // Fetch remote connections to conditionally show the remote connection step
  const { data: remoteConnectionsData, isLoading: isLoadingRemoteConnections } =
    useListRemoteConnections(organizationId, workspaceId);

  const remoteConnections = useMemo(
    () => remoteConnectionsData?.data?.remote_connections ?? [],
    [remoteConnectionsData],
  );

  const hasRemoteConnections = remoteConnections.length > 0;
  const isDraftFinalStep =
    !!wizardData.addProductionLater &&
    !requireProductionCredentials &&
    !hasRemoteConnections;

  const existingConnectionNames = useMemo(
    () => connections?.map(conn => conn.connection_name || '') ?? [],
    [connections],
  );

  // Integrations that support remote secrets reference a managed credential
  // instead of carrying values inline, and Gringotts' shared OAuth apps and
  // secrets take the place of BDK's ManagedAuthentication on step 1.
  const isManagedCredentialsEnabled = useGateValue(
    FEATURE_FLAGS.ENABLE_GRINGOTTS,
  );
  const usesRemoteSecrets =
    isManagedCredentialsEnabled && !!integration.supports_remote_secret;
  const sharedAuth = useSharedAuthentications(
    organizationId,
    workspaceId,
    integration,
    usesRemoteSecrets,
    isOpen,
  );
  const authIntegration = useMemo(
    () =>
      usesRemoteSecrets
        ? { ...integration, authentications: sharedAuth.authentications }
        : integration,
    [usesRemoteSecrets, integration, sharedAuth.authentications],
  );

  // Get credentials for the selected auth method
  // Use normalized authentication methods to match the IDs set by SelectAuthenticationMethodStep
  const normalizedAuthMethods = useMemo(
    () => normalizeAuthenticationMethods(authIntegration),
    [authIntegration],
  );

  const selectedAuth = useMemo(() => {
    if (!wizardData.authMethodId) return null;
    return normalizedAuthMethods.find(auth => {
      const authId = auth.custom?.id || auth.oauth?.id;
      return authId === wizardData.authMethodId;
    });
  }, [normalizedAuthMethods, wizardData.authMethodId]);

  // Check if OAuth authorization code flow is selected by checking flows array
  const isOAuthAuthorizationCode = selectedAuth?.oauth?.flows?.includes(
    Bdkv2OAuthFlow.OAuthFlowAuthorizationCode,
  );

  // Get the OAuth flow from the selected auth method (if it's OAuth)
  const selectedOAuthFlow = selectedAuth?.oauth?.flows?.[0];

  // Managed OAuth = OAuth authorization-code flow with no user-supplied client_id/secret
  // and no extra OAuth arguments. The user only ever clicks "Sign in with <Provider>".
  const isManagedOAuth = useMemo(
    () => !!selectedAuth && shouldShowDirectOAuthButton(selectedAuth),
    [selectedAuth],
  );

  // The shared OAuth app or secret behind the selected step-1 entry, if any.
  const sharedEntry = wizardData.authMethodId
    ? sharedAuth.entries.get(wizardData.authMethodId)
    : undefined;
  const sharedApp =
    sharedEntry?.kind === 'oauthApp' ? sharedEntry.app : undefined;
  /** Whether a step-1 auth method is backed by a managed credential type. */
  const hasSecretType = (authMethodId: string) =>
    !!normalizedAuthMethods.find(
      auth => (auth.custom?.id || auth.oauth?.id) === authMethodId,
    )?.secret_type_id;
  /** The book's own auth method id for a step-1 entry (what BDK expects). */
  const bookAuthMethodId = (authMethodId: string) =>
    sharedAuth.entries.get(authMethodId)?.rawAuthMethodId ?? authMethodId;

  const managedSecretTypeName =
    sharedEntry?.secretTypeName ?? selectedAuth?.secret_type_id ?? '';
  const usesManagedCredential = usesRemoteSecrets && !!managedSecretTypeName;

  // Signing in through an app the organization or workspace registered: the
  // book's OAuth method when no shared app covers it, or by choice behind a
  // shared app's entry.
  const [prefersOwnApp, setPrefersOwnApp] = useState(false);
  const usesOwnOAuthApp =
    usesManagedCredential &&
    ((!!selectedAuth?.oauth && !sharedEntry) || (!!sharedApp && prefersOwnApp));
  /** The book's own OAuth method, which an own app is registered from. */
  const bookOAuthAuthentication =
    sharedEntry?.kind === 'oauthApp'
      ? sharedEntry.rawAuthentication
      : selectedAuth;
  const ownOAuthApps = usesOwnOAuthApp
    ? (sharedAuth.ownAppsByType.get(managedSecretTypeName) ?? [])
    : [];
  const [chosenOwnAppId, setChosenOwnAppId] = useState<string>();
  const [ownAppError, setOwnAppError] = useState<string>();
  const selectedOwnApp =
    ownOAuthApps.find(app => app.id === chosenOwnAppId) ??
    (!chosenOwnAppId && ownOAuthApps.length === 1
      ? ownOAuthApps[0]
      : undefined);
  /** The OAuth app a new credential signs in through. */
  const activeOAuthApp = usesOwnOAuthApp ? selectedOwnApp : sharedApp;

  const {
    credentials: compatibleCredentials,
    secretType: managedSecretType,
    isLoading: isLoadingCompatibleCredentials,
    isError: isCompatibleCredentialsError,
  } = useCompatibleCredentials(
    organizationId,
    workspaceId,
    managedSecretTypeName,
    usesManagedCredential && sharedEntry?.kind !== 'secret',
  );
  const { createCredential } = useCreateCredential(organizationId, workspaceId);
  const { createOAuthCredential } = useCreateOAuthCredential(
    organizationId,
    workspaceId,
  );
  const { createConnectionWithRemoteSecret, updateConnectionWithRemoteSecret } =
    useRemoteSecretConnectionActions(organizationId, workspaceId);
  const abandonSignIn = useAbandonOAuthSignIn(organizationId, workspaceId);
  const { hasPermission } = usePermissions();
  const canChooseCredentialScope = hasPermission(['CreateSecret'], {
    organization: organizationId,
  });

  // The app's arguments without a fixed value are asked for when creating.
  const oauthArgumentDescriptors = useMemo(
    () =>
      activeOAuthApp
        ? oauthArgumentsToFill(activeOAuthApp).map(argument => ({
            id: argument.id,
            label: argument.label || argument.id,
            type: Bdkv2CredentialType.CredentialTypeText,
          }))
        : usesOwnOAuthApp
          ? []
          : undefined,
    [activeOAuthApp, usesOwnOAuthApp],
  );
  const managedFieldDescriptors = useMemo(
    () => oauthArgumentDescriptors ?? selectedAuth?.custom?.credentials ?? [],
    [oauthArgumentDescriptors, selectedAuth],
  );
  const oauthProviderName =
    (sharedApp || usesOwnOAuthApp) && selectedAuth
      ? getOAuthProviderConfig(integration, selectedAuth).name
      : undefined;

  const draftCredentialName = defaultCredentialDisplayName(
    integration.display_name || integration.name || '',
    wizardData.connectionName || '',
  );
  // Distinct from the test credential's default so the two don't collide.
  const publishedCredentialName = tIntegrations(
    'integrations.addConnection.productionCredentialName',
    { name: draftCredentialName },
  );
  // Nothing to pick: open the step straight on the new-credential form.
  const hasNoCompatibleCredentials =
    !isLoadingCompatibleCredentials &&
    !isCompatibleCredentialsError &&
    compatibleCredentials.length === 0;

  /** Creates a credential of the selected kind from the create form. */
  const createManagedCredential = async ({
    displayName,
    scope,
    values,
  }: ICredentialCreateFormValues): Promise<IResolvedCredential> => {
    if (activeOAuthApp) {
      return createOAuthCredential({
        displayName,
        secretTypeName: managedSecretTypeName,
        oauthAppId: activeOAuthApp.id ?? '',
        arguments: values,
      });
    }
    const credentialId = await createCredential({
      scope,
      displayName,
      secretTypeName: managedSecretTypeName,
      schema: managedSecretType?.schema,
      values,
    });
    return { credentialId };
  };

  const fixedCredentialId =
    sharedEntry?.kind === 'secret' ? sharedEntry.secret.id : undefined;
  const draftCredential = useManagedCredential({
    fieldDescriptors: managedFieldDescriptors,
    defaultDisplayName: draftCredentialName,
    defaultToCreate: hasNoCompatibleCredentials,
    fixedCredentialId,
    initialSelection:
      oauthResume && !oauthResume.error
        ? { mode: 'existing', credentialId: oauthResume.credentialId }
        : null,
    create: createManagedCredential,
  });
  const publishedCredential = useManagedCredential({
    fieldDescriptors: managedFieldDescriptors,
    defaultDisplayName: publishedCredentialName,
    defaultToCreate: hasNoCompatibleCredentials,
    fixedCredentialId,
    create: createManagedCredential,
  });
  const [oauthError, setOAuthError] = useState(oauthResume?.error);
  // While a managed credential is being created, the step shows only the
  // credential; the connection's own settings come once one is picked.
  const isCreatingDraftCredential =
    usesManagedCredential && draftCredential.selection?.mode === 'create';
  const isCreatingPublishedCredential =
    usesManagedCredential && publishedCredential.selection?.mode === 'create';
  // A new account through the user's own app signs in from the form, not the
  // footer; without a registered app there's nothing to sign in through yet,
  // so the step only offers registering one.
  const signsInFromOwnAppForm =
    usesOwnOAuthApp &&
    !!isOAuthAuthorizationCode &&
    draftCredential.selection?.mode === 'create';
  const showsInlineOwnAppSignIn =
    signsInFromOwnAppForm && ownOAuthApps.length > 0;

  /** The selected credential id for an environment, once one is chosen or created. */
  const selectedCredentialId = (environment: EnvironmentType) => {
    const selection =
      environment === EnvironmentType.Draft
        ? draftCredential.selection
        : publishedCredential.selection;
    return selection?.mode === 'existing' ? selection.credentialId : undefined;
  };

  /**
   * Creates a connection through the endpoint that matches the integration:
   * the remote-secret endpoint (by credential id) when it uses managed
   * credentials, the classic one (inline values) otherwise.
   */
  const addConnection = ({
    connectionName,
    authMethodId,
    credentialValues,
    credentialId,
    labels,
    configMap,
    remoteConnectionId,
  }: {
    connectionName: string;
    authMethodId: string;
    credentialValues: Record<string, string>;
    credentialId?: string;
    labels: string[];
    configMap?: Record<string, Bdkv2Value>;
    remoteConnectionId?: string;
  }) => {
    if (!usesManagedCredential) {
      return createConnection(
        integration,
        connectionName,
        authMethodId,
        credentialValues,
        labels,
        selectedOAuthFlow,
        configMap,
        remoteConnectionId,
      );
    }
    if (!credentialId) {
      throw new Error('A managed credential is required for this connection');
    }
    return createConnectionWithRemoteSecret({
      integration,
      connectionName,
      authMethodId: bookAuthMethodId(authMethodId),
      credentialId,
      labels,
      configMap,
      remoteConnectionId,
    });
  };

  /**
   * Starts sign-in for a new OAuth credential: saves the wizard so it can
   * reopen on return, then sends the browser to the provider.
   */
  const redirectToConsent = (
    authorizeUrl: string,
    credentialId: string,
    connectionName: string,
    authMethodId: string,
  ): boolean => {
    try {
      validateOAuthRedirectUrl(authorizeUrl);
    } catch (error) {
      abandonSignIn(credentialId, error);
      toast.error(tIntegrations('integrations.addConnection.oauthError'));
      return false;
    }
    const { configMap: configMapFormData } = step2Form.getValues();
    writePendingOAuthConnection({
      organizationId,
      workspaceId,
      integrationName: integration.name || '',
      integrationVersion: integration.version || '',
      connectionName,
      authMethodId,
      credentialId,
      ...(oauthOrigin ? { origin: oauthOrigin } : {}),
      // Without a wizard to reopen, the connection is created on return, as
      // BDK's own sign-in does. OAuth uses one connection for both environments.
      ...(supportsOAuthResume
        ? {}
        : {
            connectOnReturn: {
              action: 'create' as const,
              connectionName,
              authMethodId: bookAuthMethodId(authMethodId),
              labels: ['draft', 'published'],
              ...(integrationHasConfigMap && configMapFormData
                ? {
                    configMap: mapConfigMapToApiFormat(
                      configMapFormData,
                      integration.config_map,
                    ),
                  }
                : {}),
            },
          }),
    });
    onManagedSignInRedirect?.();
    window.location.assign(authorizeUrl);
    return true;
  };

  const waitForConnectionToBeReady = useWaitForConnectionToBeReady();

  // Helper function to conditionally show success toast
  const showSuccessToastMessage = async ({
    successMessage,
    connectionName,
    connectionIds,
    loadingMessage,
    errorMessage,
  }: {
    successMessage: string;
    connectionName?: string;
    connectionIds?: (string | undefined)[];
    loadingMessage?: string;
    errorMessage?: string;
  }) => {
    if (connectionIds?.every(id => id)) {
      toast.promise(
        Promise.all(
          connectionIds.map(id => waitForConnectionToBeReady(id!)),
        ).then(() => {
          queryClient.invalidateQueries({
            queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
              organizationId,
              workspaceId,
            ),
          });
        }),
        {
          loading:
            loadingMessage ||
            tIntegrations('integrations.addConnection.creatingConnection', {
              integrationName: integration.display_name,
              connectionName,
            }),
          success: showSuccessToast ? successMessage : undefined,
          error:
            errorMessage ||
            tIntegrations('integrations.addConnection.loadStateError'),
        },
      );
    }
  };

  // Get credentials list for schema creation
  // Managed OAuth has no user-entered credentials, so skip the synthetic
  // client_id/client_secret that getCredentialsForAuthMethod would otherwise add.
  // A managed credential's values live in its own create form, not the step form.
  const credentials = useMemo(
    () =>
      isManagedOAuth || usesManagedCredential
        ? []
        : getCredentialsForAuthMethod(
            selectedOAuthFlow,
            selectedAuth,
            integration.authentications,
          ),
    [
      isManagedOAuth,
      usesManagedCredential,
      selectedOAuthFlow,
      selectedAuth,
      integration.authentications,
    ],
  );

  // Check if integration has config_map
  const integrationHasConfigMap = useMemo(
    () => hasConfigMap(integration),
    [integration],
  );

  // Calculate total steps based on createSameConnection flag, config_map, and remote connections
  // Base steps: 1-3 depending on auth/config
  // +1 if remote connections exist in the org (remote connection step at the end)
  const baseSteps = hasNoAuth
    ? integrationHasConfigMap
      ? 2
      : 1
    : wizardData.createSameConnection || isOAuthAuthorizationCode
      ? 2
      : 3;

  const totalSteps = hasRemoteConnections ? baseSteps + 1 : baseSteps;
  const remoteConnectionStepNumber = hasRemoteConnections ? totalSteps : null;

  // Create schemas - combine credentials and config_map schemas
  const configMapSchema = useMemo(
    () => createConfigMapSchema(integration.config_map),
    [integration.config_map],
  );

  const step2Schema = useMemo(
    () =>
      z.object({
        ...createAuthenticationCredentialsSchema(credentials).shape,
        ...(integrationHasConfigMap ? { configMap: configMapSchema } : {}),
      }),
    [credentials, integrationHasConfigMap, configMapSchema],
  );

  const step3Schema = useMemo(
    () =>
      z.object({
        ...createAuthenticationCredentialsSchema(credentials).shape,
        ...(integrationHasConfigMap ? { configMap: configMapSchema } : {}),
      }),
    [credentials, integrationHasConfigMap, configMapSchema],
  );

  // Without a wizard to reopen after signing in, the connection's
  // configuration is asked before leaving, as BDK's own sign-in does.
  const asksConfigBeforeSignIn =
    !supportsOAuthResume && integrationHasConfigMap;

  // Create separate forms for steps 2 and 3
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic form with credentials + optional configMap
  const step2Form = useForm<any>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      ...buildCredentialsFormDefaultValues(credentials, wizardData.draftConfig),
      ...(integrationHasConfigMap
        ? {
            configMap: buildConfigMapFormDefaults(
              integration.config_map,
              undefined,
            ),
          }
        : {}),
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dynamic form with credentials + optional configMap
  const step3Form = useForm<any>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      ...buildCredentialsFormDefaultValues(
        credentials,
        wizardData.publishedConfig,
      ),
      ...(integrationHasConfigMap
        ? {
            configMap: buildConfigMapFormDefaults(
              integration.config_map,
              undefined,
            ),
          }
        : {}),
    },
  });

  // Central helper: after connection creation, wait for readiness, show success and finish
  const handleConnectionComplete = async (
    draftConn?: V2BookConnectionDescriptor,
    publishedConn?: V2BookConnectionDescriptor,
    _connectionName?: string,
  ) => {
    const uniqueConnectionIds = [
      draftConn?.connection_id,
      publishedConn?.connection_id,
    ].filter(
      (id, index, arr): id is string => !!id && arr.indexOf(id) === index,
    );

    if (uniqueConnectionIds.length > 0 && integration.discover_capable) {
      // For discover-capable integrations, wait for connection to be ready
      // so the user stays on the dialog and auto-open flow can trigger
      try {
        // Show the "testing" state inline while we wait for readiness.
        if (isAssistantEnabled) {
          setPanelConnection(draftConn ?? publishedConn);
        }
        const results = await Promise.all(
          uniqueConnectionIds.map(id => waitForConnectionToBeReady(id)),
        );
        void queryClient.invalidateQueries({
          queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
            organizationId,
            workspaceId,
          ),
        });

        const anyFailed = results.some(r => !r.isReady);
        if (anyFailed) {
          if (isAssistantEnabled) {
            // Keep the dialog open and surface the failure inline (shifu
            // explanation) so the user isn't dropped without an explanation.
            setPanelConnection(results.find(r => !r.isReady)?.connection);
            stopLoading();
            return;
          }
          // Otherwise: close the dialog; the sidebar badge shows the reason.
          handleReset();
          onCancel();
          return;
        }

        if (isAssistantEnabled) {
          // Success: show the "healthy" state briefly, then continue (close /
          // hand off to the discovered-entities flow, which shows its own
          // "Discovering…" state while discovery runs).
          setPanelConnection(results[0]?.connection);
          setOpenSidePanel(null);
          stopLoading();
          successTimerRef.current = setTimeout(() => {
            onSuccess(draftConn, publishedConn);
            handleReset();
          }, 1500);
          return;
        }
      } catch (error) {
        // Connection polling failed — close dialog, sidebar badge shows the status
        captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: {
              component: 'AddConnectionDialog',
              action: 'waitForConnectionToBeReady',
            },
          },
        );
        handleReset();
        onCancel();
        return;
      }
    }
    // Status is communicated through sidebar badges — no toasts needed

    onSuccess(draftConn, publishedConn);
    handleReset();
  };

  const resetFormsForAuthMethod = (authMethodId?: string) => {
    const newAuth = normalizedAuthMethods.find(auth => {
      const authId = auth.custom?.id || auth.oauth?.id;
      return authId === authMethodId;
    });
    const newOAuthFlow = newAuth?.oauth?.flows?.[0];
    const newIsManagedOAuth = !!newAuth && shouldShowDirectOAuthButton(newAuth);
    const newCredentials = newIsManagedOAuth
      ? []
      : getCredentialsForAuthMethod(
          newOAuthFlow,
          newAuth,
          integration.authentications,
        );
    const configMapDefaults = integrationHasConfigMap
      ? {
          configMap: buildConfigMapFormDefaults(
            integration.config_map,
            undefined,
          ),
        }
      : {};

    step2Form.reset({
      ...buildCredentialsFormDefaultValues(newCredentials, undefined),
      ...configMapDefaults,
    });
    step3Form.reset({
      ...buildCredentialsFormDefaultValues(newCredentials, undefined),
      ...configMapDefaults,
    });
    // A different method means a different credential type.
    draftCredential.reset();
    publishedCredential.reset();
    setPrefersOwnApp(false);
    setChosenOwnAppId(undefined);
  };

  const handleConnectionMethodStepSubmit = (data: ConnectionMethodStepData) => {
    // Always exit the connection method step on submit, regardless of which path
    // is taken — including the "without credentials + config_map/remote" path that
    // advances to step 2 instead of creating the connection immediately.
    setHasAdvancedPastConnectionMethodStep(true);
    if (data.useCredentials) {
      // Carry the connection name to the auth method step and show it.
      setWizardData(prev => ({ ...prev, connectionName: data.connectionName }));
    } else {
      handleConnectWithoutCredentials(data.connectionName);
    }
  };

  const handleStep1Submit = async (data: AuthenticationMethodStepData) => {
    // Clear the connect-without-credentials flag in case the user toggled
    // between that path and an auth method via Back.
    setWizardData(prev => ({
      ...prev,
      ...data,
      connectWithoutCredentials: false,
    }));
    resetFormsForAuthMethod(data.authMethodId);

    if (hasNoAuth && !integrationHasConfigMap) {
      // No auth and no config_map
      if (hasRemoteConnections) {
        // Remote connections exist — advance to remote step (which is step 2 since baseSteps=1)
        setCurrentStep(2);
      } else {
        // Create connections directly
        try {
          startLoading();
          const response = await createConnection(
            integration,
            data.connectionName,
            '',
            {},
            ['draft', 'published'],
            selectedOAuthFlow,
          );

          const connection = response.book_connection_descriptor;
          await handleConnectionComplete(
            connection,
            connection,
            data.connectionName,
          );
        } catch (error) {
          showConnectionError(
            error,
            'integrations.addConnection.error',
            'handleStep1Submit',
          );
        } finally {
          stopLoading();
        }
      }
    } else {
      setWizardData(prev => ({ ...prev, ...data }));
      setCurrentStep(2);
    }
  };

  // Get the remote connection ID to pass to createConnection (undefined if public)
  const getRemoteConnectionId = () =>
    wizardData.connectionType === ConnectionType.Remote
      ? wizardData.remoteConnectionId
      : undefined;

  const handleDraftCredentialsSubmit = async (
    data: AuthenticationCredentialsStepData & { configMap?: ConfigMapFormData },
    credentialId?: string,
  ) => {
    // Extract config_map from form data
    const { configMap: configMapFormData, ...credentialsData } = data;

    // Convert config_map form values to API format
    const configMapApiData =
      integrationHasConfigMap && configMapFormData
        ? mapConfigMapToApiFormat(configMapFormData, integration.config_map)
        : undefined;

    if (
      wizardData.createSameConnection ||
      (hasNoAuth && integrationHasConfigMap)
    ) {
      // Single connection mode
      if (hasRemoteConnections) {
        // Defer creation — store data and advance to remote step
        setWizardData(prev => ({
          ...prev,
          draftConfig: credentialsData,
          storedConfigMapData: configMapApiData,
        }));
        setCurrentStep(totalSteps);
        return;
      }

      // No remote — create with both labels immediately, or update the one a
      // failed check left behind (the dialog stays open for a fix-and-retry).
      try {
        startLoading();
        const response = wizardData.sameConnectionId
          ? usesManagedCredential
            ? await updateConnectionWithRemoteSecret({
                integration,
                connectionId: wizardData.sameConnectionId,
                connectionName: wizardData.connectionName!,
                authMethodId: bookAuthMethodId(wizardData.authMethodId || ''),
                credentialId: credentialId!,
                labels: ['draft', 'published'],
                configMap: configMapApiData,
              })
            : await updateConnection(
                {
                  connection_id: wizardData.sameConnectionId,
                  book_name: integration.name,
                  book_version: integration.version,
                  connection_name: wizardData.connectionName,
                  labels: [
                    { key: 'draft_connection', value: 'true' },
                    { key: 'published_connection', value: 'true' },
                  ],
                  authentication: {
                    authentication_id: wizardData.authMethodId,
                  },
                },
                credentialsData,
                integration,
                configMapApiData,
              )
          : await addConnection({
              connectionName: wizardData.connectionName!,
              authMethodId: wizardData.authMethodId || '',
              credentialValues: credentialsData,
              credentialId,
              labels: ['draft', 'published'],
              configMap: configMapApiData,
            });

        const connection = response.book_connection_descriptor;
        setWizardData(prev => ({
          ...prev,
          sameConnectionId: connection?.connection_id,
        }));
        await handleConnectionComplete(connection, connection);
      } catch (error) {
        showConnectionError(error);
      } finally {
        stopLoading();
      }
    } else {
      // Two-step mode
      if (hasRemoteConnections) {
        // Defer creation — store draft data and advance to published step or remote step
        setWizardData(prev => ({
          ...prev,
          draftConfig: credentialsData,
          storedConfigMapData: configMapApiData,
        }));
        if (wizardData.addProductionLater && !requireProductionCredentials) {
          // Skip published step, go to remote step
          setCurrentStep(totalSteps);
        } else {
          if (integrationHasConfigMap && configMapFormData) {
            step3Form.setValue('configMap', configMapFormData);
          }
          setCurrentStep(3);
        }
        return;
      }

      // No remote — create or update draft immediately
      try {
        startLoading();

        if (wizardData.draftConnectionId) {
          // Update existing draft connection
          const updateResponse = usesManagedCredential
            ? await updateConnectionWithRemoteSecret({
                integration,
                connectionId: wizardData.draftConnectionId,
                connectionName: wizardData.connectionName!,
                authMethodId: bookAuthMethodId(wizardData.authMethodId!),
                credentialId: credentialId!,
                labels: ['draft'],
                configMap: configMapApiData,
              })
            : await updateConnection(
                {
                  connection_id: wizardData.draftConnectionId,
                  book_name: integration.name,
                  book_version: integration.version,
                  connection_name: wizardData.connectionName,
                  labels: [{ key: 'draft_connection', value: 'true' }],
                  authentication: {
                    authentication_id: wizardData.authMethodId,
                  },
                },
                credentialsData,
                integration,
                configMapApiData,
              );
          setCreatedDraftConnection(updateResponse.book_connection_descriptor);
          if (isDraftFinalStep) {
            // Same as the create path below: with Production deferred this is
            // the last step, so re-check and finish (or surface the failure).
            await handleConnectionComplete(
              updateResponse.book_connection_descriptor,
              undefined,
            );
          } else {
            showSuccessToastMessage({
              successMessage: tIntegrations(
                'integrations.addConnection.draftUpdated',
              ),
              connectionName: wizardData.connectionName,
              loadingMessage: tIntegrations(
                'integrations.addConnection.updatingDraft',
              ),
              connectionIds: [
                updateResponse.book_connection_descriptor?.connection_id,
              ],
            });
          }
        } else {
          // Create new draft connection
          const draftResponse = await addConnection({
            connectionName: wizardData.connectionName!,
            authMethodId: wizardData.authMethodId!,
            credentialValues: credentialsData,
            credentialId,
            labels: ['draft'],
            configMap: configMapApiData,
          });

          // Store draft connection
          setCreatedDraftConnection(draftResponse.book_connection_descriptor);

          // Store draft connection ID for potential updates
          setWizardData(prev => ({
            ...prev,
            draftConfig: credentialsData,
            draftConnectionId:
              draftResponse.book_connection_descriptor?.connection_id,
          }));
          // If "Add production later" is checked, close modal instead of proceeding to published step
          // Unless requireProductionCredentials is true, then always go to published step
          if (isDraftFinalStep) {
            await handleConnectionComplete(
              draftResponse.book_connection_descriptor,
              undefined,
            );
          }
        }
        if (!isDraftFinalStep) {
          if (integrationHasConfigMap && configMapFormData) {
            step3Form.setValue('configMap', configMapFormData);
          }
          setCurrentStep(3);
        }
      } catch (error) {
        showConnectionError(
          error,
          wizardData.draftConnectionId
            ? 'integrations.addConnection.draftUpdateError'
            : 'integrations.addConnection.draftError',
        );
      } finally {
        stopLoading();
        queryClient.invalidateQueries({
          queryKey: getRetrieveAllWorkspaceBookConnectionsQueryKey(
            organizationId,
            workspaceId,
          ),
        });
      }
    }
  };

  const handlePublishedCredentialsSubmit = async (
    data: AuthenticationCredentialsStepData & { configMap?: ConfigMapFormData },
    credentialId?: string,
  ) => {
    // Extract config_map from form data
    const { configMap: configMapFormData, ...credentialsData } = data;

    // Convert config_map form values to API format
    const configMapApiData =
      integrationHasConfigMap && configMapFormData
        ? mapConfigMapToApiFormat(configMapFormData, integration.config_map)
        : undefined;

    if (hasRemoteConnections) {
      // Defer creation — store published data and advance to remote step
      setWizardData(prev => ({
        ...prev,
        publishedConfig: credentialsData,
        storedConfigMapData: configMapApiData ?? prev.storedConfigMapData,
      }));
      setCurrentStep(totalSteps);
      return;
    }

    // No remote — create the published connection immediately, or update the
    // one a failed check left behind.
    try {
      startLoading();
      const publishedResponse = wizardData.publishedConnectionId
        ? usesManagedCredential
          ? await updateConnectionWithRemoteSecret({
              integration,
              connectionId: wizardData.publishedConnectionId,
              connectionName: wizardData.connectionName!,
              authMethodId: bookAuthMethodId(wizardData.authMethodId!),
              credentialId: credentialId!,
              labels: ['published'],
              configMap: configMapApiData,
            })
          : await updateConnection(
              {
                connection_id: wizardData.publishedConnectionId,
                book_name: integration.name,
                book_version: integration.version,
                connection_name: wizardData.connectionName,
                labels: [{ key: 'published_connection', value: 'true' }],
                authentication: {
                  authentication_id: wizardData.authMethodId,
                },
              },
              credentialsData,
              integration,
              configMapApiData,
            )
        : await addConnection({
            connectionName: wizardData.connectionName!,
            authMethodId: wizardData.authMethodId!,
            credentialValues: credentialsData,
            credentialId,
            labels: ['published'],
            configMap: configMapApiData,
          });
      setWizardData(prev => ({
        ...prev,
        publishedConnectionId:
          publishedResponse.book_connection_descriptor?.connection_id,
      }));

      // Return both draft and published connections
      await handleConnectionComplete(
        createdDraftConnection,
        publishedResponse.book_connection_descriptor,
      );
    } catch (error) {
      const classification = classifyDuplicateCredentialsError(
        error,
        createdDraftConnection?.connection_id,
      );
      if (classification.kind === 'ownDraftDuplicate') {
        // Expected: the published credentials match the wizard's own draft, so
        // we offer to reuse it. Not an error worth reporting.
        const { configMap: _ignoredConfigMap, ...credsSnapshot } =
          step3Form.getValues();
        setReuseAlertSnapshot(credsSnapshot);
      } else {
        // showConnectionError reports genuine failures and skips other
        // duplicate-credentials cases.
        showConnectionError(error, 'integrations.addConnection.publishedError');
      }
    } finally {
      stopLoading();
    }
  };

  const handleReuseTestConnection = async () => {
    // The Reuse alert is only raised when the backend confirms the wizard's
    // own draft connection holds these credentials, so createdDraftConnection
    // and draftConfig are guaranteed populated at this point.
    if (!wizardData.draftConfig || !createdDraftConnection) return;

    try {
      startLoading();
      const response = await promoteDraftToProductionConnection({
        updateConnection,
        draftConnection: createdDraftConnection,
        credentials: wizardData.draftConfig,
        integration,
        configMap: wizardData.storedConfigMapData,
      });
      const conn = response.book_connection_descriptor;
      await handleConnectionComplete(conn, conn);
    } catch (error) {
      showConnectionError(
        error,
        'integrations.addConnection.error',
        'reuseTestConnection',
      );
    } finally {
      stopLoading();
    }
  };

  const handleBack = () => {
    // When on the auth method step (step 1, not showing connection method step),
    // go back to the connection method step instead of closing the dialog.
    if (
      currentStep === 1 &&
      !isShowingConnectionMethodStep &&
      showConnectionMethodStep
    ) {
      setHasAdvancedPastConnectionMethodStep(false);
      return;
    }
    // A new credential is a screen within the credential step: when there are
    // credentials to pick from, Back returns to them.
    const managedCredential =
      currentStep === 2
        ? draftCredential
        : currentStep === 3
          ? publishedCredential
          : undefined;
    if (
      usesManagedCredential &&
      managedCredential?.selection?.mode === 'create' &&
      compatibleCredentials.length > 0
    ) {
      setOwnAppError(undefined);
      if (currentStep === 2) setPrefersOwnApp(false);
      managedCredential.setSelection(null);
      return;
    }
    if (currentStep > 1) {
      // Navigating away from step 3 invalidates any prior duplicate-credentials
      // snapshot: the user is either going to edit the draft on step 2 (which
      // changes the conflict) or just retry. Drop the alert so the Continue /
      // Connect buttons aren't stuck disabled.
      resetReuseAlert();
      setCurrentStep(currentStep - 1);
    }
  };

  const handleReset = () => {
    setCurrentStep(1);
    setWizardData({});
    step2Form.reset();
    step3Form.reset();
    draftCredential.reset();
    publishedCredential.reset();
    setCreatedDraftConnection(undefined);
    setPanelConnection(undefined);
    clearTimeout(successTimerRef.current);
    setOpenSidePanel(null);
    setDiagnosedConnectionId(undefined);
    resetReuseAlert();
    setHasAdvancedPastConnectionMethodStep(false);
    setConnectionMethodStepData({ connectionName: '', useCredentials: false });
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const handleOAuthButtonClick = useCallback(async () => {
    const isValid = await step2Form.trigger();
    if (isValid && wizardData.connectionName && wizardData.authMethodId) {
      setIsOAuthLoading(true);
      try {
        // Extract configMap from form values to pass separately
        const { configMap: configMapFormData, ...credentialsData } =
          step2Form.getValues();

        // Convert config_map form values to API format
        const configMapApiData =
          integrationHasConfigMap && configMapFormData
            ? mapConfigMapToApiFormat(configMapFormData, integration.config_map)
            : undefined;

        await onOAuthClick?.(
          wizardData.authMethodId,
          wizardData.connectionName,
          credentialsData,
          ['draft', 'published'],
          configMapApiData,
        );
      } catch (error) {
        captureException(error as Error, {
          tags: { module: 'integrations', action: 'oauth_click' },
        });
        toast.error(tIntegrations('integrations.addConnection.oauthError'));
      } finally {
        setIsOAuthLoading(false);
      }
    }
  }, [
    step2Form,
    wizardData.connectionName,
    wizardData.authMethodId,
    onOAuthClick,
    tIntegrations,
    integrationHasConfigMap,
    integration.config_map,
  ]);

  /**
   * Creates a credential bound to a shared OAuth app under its default name and
   * sends the browser to the provider to sign in.
   */
  const signInWithSharedApp = async (
    entry: TSharedAuthEntry & { kind: 'oauthApp' },
    connectionName: string,
  ) => {
    if (asksConfigBeforeSignIn && !(await step2Form.trigger('configMap'))) {
      return;
    }
    setIsOAuthLoading(true);
    try {
      // No name field here to report a conflict on, so take the next free name.
      const { credentialId, authorizeUrl } = await createWithAvailableName(
        defaultCredentialDisplayName(
          integration.display_name || integration.name || '',
          connectionName,
        ),
        displayName =>
          createOAuthCredential({
            displayName,
            secretTypeName: entry.secretTypeName,
            oauthAppId: entry.app.id ?? '',
            arguments: {},
          }),
      );
      if (!authorizeUrl) {
        draftCredential.setSelection({ mode: 'existing', credentialId });
        setCurrentStep(2);
        setIsOAuthLoading(false);
        return;
      }
      if (
        !redirectToConsent(authorizeUrl, credentialId, connectionName, entry.id)
      ) {
        setIsOAuthLoading(false);
      }
    } catch (error) {
      captureException(error as Error, {
        tags: { module: 'integrations', action: 'sharedAppSignIn' },
      });
      toast.error(tIntegrations('integrations.addConnection.oauthError'));
      setIsOAuthLoading(false);
    }
  };

  /**
   * "Sign in with …" on a shared OAuth app at step 1. Goes straight to the
   * provider when there is nothing to ask; otherwise opens the credential step,
   * which offers the accounts already signed in through this app and the app's
   * arguments.
   */
  const handleSharedAppSignIn = async (
    authMethodId: string,
    connectionName: string,
  ) => {
    const entry = sharedAuth.entries.get(authMethodId);
    if (entry?.kind !== 'oauthApp') return;
    setWizardData(prev => ({
      ...prev,
      connectionName,
      authMethodId,
      createSameConnection: true,
      connectWithoutCredentials: false,
    }));
    resetFormsForAuthMethod(authMethodId);
    setOAuthError(undefined);

    const hasSignedInAccounts =
      (sharedAuth.credentialsByType.get(entry.secretTypeName) ?? []).length > 0;
    if (
      hasSignedInAccounts ||
      oauthArgumentsToFill(entry.app).length > 0 ||
      asksConfigBeforeSignIn
    ) {
      setCurrentStep(2);
      return;
    }
    await signInWithSharedApp(entry, connectionName);
  };

  /**
   * "Use your organization's own OAuth app" under a shared app's step-1 entry,
   * or "Sign in with …" on an OAuth method no shared app covers: opens the
   * credential step on a new account through an own app.
   */
  const handleUseOwnOAuthApp = (
    authMethodId: string,
    connectionName: string,
  ) => {
    setWizardData(prev => ({
      ...prev,
      connectionName,
      authMethodId,
      createSameConnection: true,
      connectWithoutCredentials: false,
    }));
    resetFormsForAuthMethod(authMethodId);
    setOAuthError(undefined);
    setPrefersOwnApp(true);
    draftCredential.setSelection({ mode: 'create' });
    setCurrentStep(2);
  };

  /**
   * "Sign in with …" on the credential step: another account through the same
   * app. Asks for the app's arguments first when it has any.
   */
  const handleSignInAgain = async () => {
    if (sharedEntry?.kind !== 'oauthApp') return;
    setOAuthError(undefined);
    if (oauthArgumentsToFill(sharedEntry.app).length > 0) {
      draftCredential.setSelection({ mode: 'create' });
      return;
    }
    await signInWithSharedApp(sharedEntry, wizardData.connectionName || '');
  };

  const handleSelectStepOAuthClick = useCallback(
    async (authMethodId: string, connectionName: string) => {
      // When the integration has a config_map, prompt for it before redirecting
      // to the OAuth provider — otherwise the user has no chance to enter required
      // configuration. Step 2 already renders config_map + the OAuth sign-in button.
      if (integrationHasConfigMap) {
        setWizardData(prev => ({
          ...prev,
          connectionName,
          authMethodId,
          createSameConnection: true,
          connectWithoutCredentials: false,
        }));
        resetFormsForAuthMethod(authMethodId);
        setCurrentStep(2);
        return;
      }

      // Don't save wizard data - we're redirecting immediately
      setIsOAuthLoading(true);

      try {
        // Trigger OAuth redirect (this will navigate away from the page)
        await onOAuthClick?.(authMethodId, connectionName, {}, [
          'draft',
          'published',
        ]);
        // Success - close dialog before redirect happens
        onCancel();
      } catch (error) {
        // If OAuth fails, reset loading state and keep dialog open
        captureException(error as Error, {
          tags: { module: 'integrations', action: 'selectStepOAuthClick' },
        });
        setIsOAuthLoading(false);
        onCancel();
        // Error is already handled in parent's handleOAuthClick
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resetFormsForAuthMethod is a stable intra-component function
    [integrationHasConfigMap, onOAuthClick, onCancel],
  );

  /**
   * Handler for connecting without credentials.
   * Creates a connection with empty auth method and credentials.
   * Only available when integration.connection_required is false.
   */
  const handleConnectWithoutCredentials = useCallback(
    async (connectionName: string) => {
      if (hasRemoteConnections) {
        // Need to collect remote connection selection first — advance to remote step (last step)
        setWizardData(prev => ({
          ...prev,
          connectionName,
          authMethodId: '',
          createSameConnection: true,
        }));
        // baseSteps=1 for no-auth no-config, so totalSteps=2, remote step is step 2
        setCurrentStep(2);
        return;
      }

      // Integration has a config_map — collect it on step 2 before creating the
      // connection. Without this, picking "Connect Without Credentials" would
      // skip the config the integration needs.
      if (integrationHasConfigMap) {
        setWizardData(prev => ({
          ...prev,
          connectionName,
          authMethodId: '',
          createSameConnection: true,
          connectWithoutCredentials: true,
        }));
        resetFormsForAuthMethod('');
        setCurrentStep(2);
        return;
      }

      try {
        startLoading();
        const response = await createConnection(
          integration,
          connectionName,
          '',
          {},
          ['draft', 'published'],
        );

        // When both labels are used, the connection serves as both draft and published
        const connection = response.book_connection_descriptor;
        await handleConnectionComplete(connection, connection, connectionName);
      } catch (error) {
        showConnectionError(
          error,
          'integrations.addConnection.error',
          'connectWithoutCredentials',
        );
      } finally {
        stopLoading();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleReset doesn't need to be a dependency as it's a stable function
    [
      integration,
      createConnection,
      onSuccess,
      showSuccessToastMessage,
      tIntegrations,
      hasRemoteConnections,
      integrationHasConfigMap,
    ],
  );

  const handleConnectionTypeChange = useCallback((type: ConnectionType) => {
    setWizardData(prev => ({
      ...prev,
      connectionType: type,
      remoteConnectionId:
        type === ConnectionType.Public ? undefined : prev.remoteConnectionId,
    }));
  }, []);

  const handleRemoteConnectionSelect = useCallback((id: string) => {
    setWizardData(prev => ({ ...prev, remoteConnectionId: id }));
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Dialog is being closed - reset the state
      handleReset();
      onCancel();
    }
  };

  const handleDialogOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
  }, []);

  // Handler for the remote connection step (last step)
  // Creates all connections using stored wizard data + remote_connection_id
  const handleRemoteConnectionStepSubmit = async () => {
    const remoteConnectionId = getRemoteConnectionId();

    try {
      startLoading();

      const isSingleConnection =
        wizardData.createSameConnection ||
        (hasNoAuth && !wizardData.publishedConfig) ||
        wizardData.addProductionLater;

      if (isSingleConnection) {
        // Single connection mode — create with both labels (or just draft if addProductionLater)
        const labels =
          wizardData.addProductionLater && !requireProductionCredentials
            ? ['draft']
            : ['draft', 'published'];

        const response = await addConnection({
          connectionName: wizardData.connectionName!,
          authMethodId: wizardData.authMethodId || '',
          credentialValues: wizardData.draftConfig || {},
          credentialId: selectedCredentialId(EnvironmentType.Draft),
          labels,
          configMap: wizardData.storedConfigMapData,
          remoteConnectionId,
        });

        const connection = response.book_connection_descriptor;
        if (labels.includes('published')) {
          await handleConnectionComplete(connection, connection);
        } else {
          await handleConnectionComplete(connection, undefined);
        }
      } else {
        // Two-step mode — create draft and published separately
        const [draftResponse, publishedResponse] = await Promise.all([
          addConnection({
            connectionName: wizardData.connectionName!,
            authMethodId: wizardData.authMethodId!,
            credentialValues: wizardData.draftConfig || {},
            credentialId: selectedCredentialId(EnvironmentType.Draft),
            labels: ['draft'],
            configMap: wizardData.storedConfigMapData,
            remoteConnectionId,
          }),
          addConnection({
            connectionName: wizardData.connectionName!,
            authMethodId: wizardData.authMethodId!,
            credentialValues: wizardData.publishedConfig || {},
            credentialId: selectedCredentialId(EnvironmentType.Published),
            labels: ['published'],
            configMap: wizardData.storedConfigMapData,
            remoteConnectionId,
          }),
        ]);

        await handleConnectionComplete(
          draftResponse.book_connection_descriptor,
          publishedResponse.book_connection_descriptor,
        );
      }
    } catch (error) {
      showConnectionError(error);
    } finally {
      stopLoading();
    }
  };

  const keyValueCredentialIds = getKeyValueCredentialIds(
    integration,
    wizardData.authMethodId || '',
  );
  const { confirmEmptyValues, dialogProps: emptyCredentialValuesDialogProps } =
    useEmptyCredentialValuesConfirmation();

  const submitDraftCredentials = (
    data: AuthenticationCredentialsStepData & { configMap?: ConfigMapFormData },
  ) =>
    confirmEmptyValues(
      keyValueCredentialIds.map(id => data[id]),
      () => void handleDraftCredentialsSubmit(data),
    );

  const submitPublishedCredentials = (
    data: AuthenticationCredentialsStepData & { configMap?: ConfigMapFormData },
  ) =>
    confirmEmptyValues(
      keyValueCredentialIds.map(id => data[id]),
      () => void handlePublishedCredentialsSubmit(data),
    );

  /**
   * Submits a credential step for an integration using managed credentials:
   * validates the step (config map), resolves the picked credential — creating
   * it first when "create new" is selected — then creates the connection.
   */
  const submitManagedCredentialStep = (environment: EnvironmentType) => {
    const isDraft = environment === EnvironmentType.Draft;
    const stepForm = isDraft ? step2Form : step3Form;
    const managedCredential = isDraft ? draftCredential : publishedCredential;
    const submit = isDraft
      ? handleDraftCredentialsSubmit
      : handlePublishedCredentialsSubmit;

    const isCreating = managedCredential.selection?.mode === 'create';
    if (usesOwnOAuthApp && isCreating && !selectedOwnApp) {
      setOwnAppError(tCredentials('credentials.oauthApp.selectionRequired'));
      return;
    }

    /**
     * Resolves the credential, creating it when needed. Returns null when the
     * browser is leaving to sign in, or nothing could be resolved.
     */
    const resolveCredential = async () => {
      setOAuthError(undefined);
      startLoading();
      const resolved = await managedCredential.resolveCredential();
      if (resolved?.authorizeUrl) {
        // Stays loading while the browser leaves for the provider.
        if (
          !redirectToConsent(
            resolved.authorizeUrl,
            resolved.credentialId,
            wizardData.connectionName || '',
            wizardData.authMethodId || '',
          )
        ) {
          stopLoading();
          managedCredential.reset();
        }
        return null;
      }
      stopLoading();
      return resolved;
    };

    // The configuration belongs to the connection and is asked for once a
    // credential is picked, so creating one only creates the credential; the
    // step then lists it, selected, with the configuration and Connect.
    /** Asks before creating a credential whose key/value pairs have empty values. */
    const confirmNewValues = (proceed: () => void) => {
      if (!isCreating) {
        proceed();
        return;
      }
      confirmEmptyValues(
        keyValueCredentialIds.map(id =>
          managedCredential.form.getValues(`values.${id}`),
        ),
        proceed,
      );
    };

    if (isCreating && integrationHasConfigMap) {
      confirmNewValues(async () => {
        if (
          asksConfigBeforeSignIn &&
          isOAuthAuthorizationCode &&
          !(await stepForm.trigger('configMap'))
        ) {
          return;
        }
        await resolveCredential();
      });
      return;
    }

    void stepForm.handleSubmit(data =>
      confirmNewValues(async () => {
        const resolved = await resolveCredential();
        if (resolved) await submit(data, resolved.credentialId);
      }),
    )();
  };

  const handleNext = () => {
    if (isShowingConnectionMethodStep) {
      const form = document.getElementById('connection-method-form');
      if (form) {
        form.dispatchEvent(
          new Event('submit', { bubbles: true, cancelable: true }),
        );
      }
      return;
    }

    // For step 1, we let the NoAuthConnectionStep or SelectAuthenticationMethodStep handle submission
    // For the remote connection step, call its handler to create connections
    // For credential steps, use the form's handleSubmit directly
    if (currentStep === 1) {
      const formId = 'connection-name-and-auth-method-form';
      const form = document.getElementById(formId);
      if (form) {
        const submitEvent = new Event('submit', {
          bubbles: true,
          cancelable: true,
        });
        form.dispatchEvent(submitEvent);
      }
    } else if (currentStep === remoteConnectionStepNumber) {
      handleRemoteConnectionStepSubmit();
    } else if (currentStep === 2) {
      if (usesManagedCredential) {
        submitManagedCredentialStep(EnvironmentType.Draft);
      } else {
        step2Form.handleSubmit(submitDraftCredentials)();
      }
    } else if (currentStep === 3) {
      if (usesManagedCredential) {
        submitManagedCredentialStep(EnvironmentType.Published);
      } else {
        step3Form.handleSubmit(submitPublishedCredentials)();
      }
    }
  };

  const getButtonText = () => {
    if (isShowingConnectionMethodStep) {
      if (connectionMethodStepData.useCredentials) {
        return tCommon('common.action.continue');
      }
      // "Connect" only when the connection is created immediately (no extra steps).
      // If there's a config_map or remote connections, we still need another step.
      const connectsImmediately =
        !integrationHasConfigMap && !hasRemoteConnections;
      return connectsImmediately
        ? tCommon('common.action.connect')
        : tCommon('common.action.continue');
    }

    if (currentStep === remoteConnectionStepNumber) {
      return tCommon('common.action.connect');
    }

    if (hasNoAuth && currentStep === 1) {
      return tCommon('common.action.connect');
    }

    // With a configuration to fill in afterwards, creating a credential is its
    // own action (see submitManagedCredentialStep).
    if (
      (currentStep === 2 && isCreatingDraftCredential) ||
      (currentStep === 3 && isCreatingPublishedCredential)
    ) {
      if (integrationHasConfigMap && !isOAuthAuthorizationCode) {
        return tCredentials('credentials.create.submit');
      }
    }

    if (currentStep === 2) {
      // A new account for an authorization-code app is created by signing in.
      if (
        (sharedApp || usesOwnOAuthApp) &&
        isOAuthAuthorizationCode &&
        draftCredential.selection?.mode === 'create'
      ) {
        return tIntegrations('integrations.oauth.signInButton', {
          oauthProvider: oauthProviderName,
        });
      }
      // If we have a draft connection ID and we're on step 2, we're updating
      if (wizardData.draftConnectionId) {
        if (isDraftFinalStep) {
          return tCommon('common.action.connect');
        }
        return tIntegrations(
          'integrations.addConnection.updateDraftAndContinue',
        );
      }
      // Single connection mode without remote connections is the final step
      if (wizardData.createSameConnection && !hasRemoteConnections) {
        return tCommon('common.action.connect');
      }
      return tCommon('common.action.continue');
    }

    if (currentStep === 3) {
      // If remote connections exist, step 3 (published creds) is not the final step
      if (hasRemoteConnections) {
        return tCommon('common.action.continue');
      }
      return tCommon('common.action.connect');
    }

    return tCommon('common.action.continue');
  };

  const canGoBack =
    currentStep > 1 ||
    (currentStep === 1 &&
      !isShowingConnectionMethodStep &&
      showConnectionMethodStep);
  // For OAuth authorization code, don't show Next button on step 2 (OAuth button is shown instead)
  // Classic authorization-code OAuth swaps the footer on step 2 for its own
  // sign-in button; managed credentials sign in from the footer instead.
  const hidesFooterForClassicOAuth =
    isOAuthAuthorizationCode && currentStep === 2 && !usesManagedCredential;
  // Signing in through the user's own app happens from the inline button.
  const hidesFooterForInlineSignIn = signsInFromOwnAppForm && currentStep === 2;
  const canGoNext =
    currentStep < totalSteps &&
    !hidesFooterForClassicOAuth &&
    !hidesFooterForInlineSignIn;

  // A managed credential's key/value fields live in its create form.
  const managedKeyValueFields = keyValueCredentialIds.map(
    id => `values.${id}` as const,
  );
  const watchedStep2KeyValueCreds = usesManagedCredential
    ? draftCredential.form.watch(managedKeyValueFields)
    : step2Form.watch(keyValueCredentialIds);
  const watchedStep3KeyValueCreds = usesManagedCredential
    ? publishedCredential.form.watch(managedKeyValueFields)
    : step3Form.watch(keyValueCredentialIds);
  const hasInvalidCredentialKeys =
    (currentStep === 2 &&
      hasInvalidKeyValueCredentials(watchedStep2KeyValueCreds)) ||
    (currentStep === 3 &&
      hasInvalidKeyValueCredentials(watchedStep3KeyValueCreds));

  // Managed credential steps need a picked (or to-be-created) credential.
  const isCredentialStep =
    (currentStep === 2 || currentStep === 3) &&
    currentStep !== remoteConnectionStepNumber;
  const isManagedCredentialMissing =
    usesManagedCredential &&
    isCredentialStep &&
    !(currentStep === 2 ? draftCredential : publishedCredential).selection;

  const watchedStep3Values = step3Form.watch();
  const { showReuseAlert, setReuseAlertSnapshot, resetReuseAlert } =
    useReuseTestConnectionAlert(watchedStep3Values);

  const renderContent = () => {
    const showsSeparator = currentStep > 1 && !!wizardData.connectionName;
    const showsEnvStatus =
      currentStep > 1 &&
      !wizardData.createSameConnection &&
      !isOAuthAuthorizationCode &&
      currentStep !== remoteConnectionStepNumber;

    return (
      <>
        {showsSeparator && (
          <div className="shrink-0 space-y-6">
            <Flex direction="column" gap={1}>
              <Text>
                {tIntegrations(
                  'integrations.addConnection.connectionName.label',
                )}
                :{' '}
                <Text as="span" weight="medium">
                  {wizardData.connectionName}
                </Text>
              </Text>
            </Flex>
            <Separator />
          </div>
        )}

        {/* Environment status stays fixed above the scrollable area */}
        {showsEnvStatus && (
          <div className="shrink-0 -mx-6 px-6 pb-4">
            <EnvironmentStatus
              activeEnvironment={
                currentStep === 2
                  ? EnvironmentType.Draft
                  : EnvironmentType.Published
              }
              onStepClick={stepKey => {
                if (stepKey === 'published' && currentStep === 2) {
                  handleNext();
                } else if (stepKey === 'draft' && currentStep === 3) {
                  handleBack();
                }
              }}
            />
          </div>
        )}

        <div
          className={`flex-1 min-h-0 overflow-y-auto -mx-6 px-6 [mask-image:linear-gradient(to_bottom,black_calc(100%_-_2rem),transparent)] pb-6 ${showsSeparator && !showsEnvStatus ? 'pt-4' : ''}`}
        >
          {currentStep === 1 &&
            (isShowingConnectionMethodStep ? (
              <ConnectionMethodStep
                existingConnectionNames={existingConnectionNames}
                defaultValues={connectionMethodStepData}
                onChange={setConnectionMethodStepData}
                onSubmit={handleConnectionMethodStepSubmit}
              />
            ) : hasNoAuth ? (
              <NoAuthConnectionStep
                integrations={integration}
                defaultValues={wizardData || {}}
                onSubmit={handleStep1Submit}
              />
            ) : (
              <SelectAuthenticationMethodStep
                integration={authIntegration}
                defaultValues={wizardData || {}}
                onSubmit={handleStep1Submit}
                onOAuthClick={async (authMethodId, connectionName) => {
                  if (sharedAuth.entries.has(authMethodId)) {
                    await handleSharedAppSignIn(authMethodId, connectionName);
                  } else if (usesRemoteSecrets && hasSecretType(authMethodId)) {
                    // No shared app covers it: sign in through an own app.
                    handleUseOwnOAuthApp(authMethodId, connectionName);
                  } else {
                    await handleSelectStepOAuthClick(
                      authMethodId,
                      connectionName,
                    );
                  }
                }}
                onUseOwnOAuthApp={handleUseOwnOAuthApp}
                canUseOwnOAuthApp={authMethodId =>
                  sharedAuth.entries.get(authMethodId)?.kind === 'oauthApp'
                }
                existingConnectionNames={existingConnectionNames}
              />
            ))}
          {currentStep > 1 && (
            <Flex direction="column" gap={4}>
              {currentStep === 2 &&
                currentStep !== remoteConnectionStepNumber &&
                (wizardData.authMethodId ||
                  (hasNoAuth && integrationHasConfigMap) ||
                  wizardData.connectWithoutCredentials) && (
                  <>
                    {usesManagedCredential && (
                      <>
                        {oauthError && (
                          <Alert variant="destructive" className="mb-4">
                            <Icon type="AlertTriangle" size="sm" />
                            <AlertDescription>
                              {tCredentials('credentials.oauth.consentFailed')}
                            </AlertDescription>
                          </Alert>
                        )}
                        <ManagedCredentialStep
                          integration={integration}
                          authMethodId={wizardData.authMethodId!}
                          idPrefix="draft-credential"
                          credentials={compatibleCredentials}
                          isLoading={isLoadingCompatibleCredentials}
                          isError={isCompatibleCredentialsError}
                          selection={draftCredential.selection}
                          onSelectionChange={selection => {
                            if (selection?.mode !== 'create') {
                              setPrefersOwnApp(false);
                            }
                            draftCredential.setSelection(selection);
                          }}
                          form={draftCredential.form}
                          defaultDisplayName={draftCredentialName}
                          canChooseScope={canChooseCredentialScope}
                          oauthArguments={oauthArgumentDescriptors}
                          oauthProviderName={oauthProviderName}
                          oauthSignIn={
                            sharedApp && !prefersOwnApp && selectedAuth
                              ? {
                                  authentication: selectedAuth,
                                  onClick: () => void handleSignInAgain(),
                                  isLoading: isOAuthLoading,
                                }
                              : undefined
                          }
                          appPicker={
                            usesOwnOAuthApp && bookOAuthAuthentication ? (
                              <OAuthAppPicker
                                integration={integration}
                                authentication={bookOAuthAuthentication}
                                secretTypeName={managedSecretTypeName}
                                apps={ownOAuthApps}
                                selectedAppId={selectedOwnApp?.id}
                                onSelect={appId => {
                                  setChosenOwnAppId(appId);
                                  setOwnAppError(undefined);
                                }}
                                canChooseScope={canChooseCredentialScope}
                                error={ownAppError}
                              />
                            ) : undefined
                          }
                          inlineSignIn={
                            showsInlineOwnAppSignIn && selectedAuth ? (
                              <OAuthSignInButton
                                integration={integration}
                                authentication={selectedAuth}
                                disabled={!selectedOwnApp}
                                isLoading={isLoading}
                                onClick={() =>
                                  submitManagedCredentialStep(
                                    EnvironmentType.Draft,
                                  )
                                }
                              />
                            ) : undefined
                          }
                          ownAppOption={
                            sharedApp && !prefersOwnApp
                              ? () => {
                                  setOwnAppError(undefined);
                                  setPrefersOwnApp(true);
                                  draftCredential.setSelection({
                                    mode: 'create',
                                  });
                                }
                              : undefined
                          }
                          managedCredentialName={
                            sharedEntry?.kind === 'secret'
                              ? sharedEntry.secret.display_name
                              : undefined
                          }
                        />
                      </>
                    )}
                    <Form {...step2Form}>
                      <form
                        onSubmit={event => {
                          if (usesManagedCredential) {
                            event.preventDefault();
                            submitManagedCredentialStep(EnvironmentType.Draft);
                            return;
                          }
                          void step2Form.handleSubmit(submitDraftCredentials)(
                            event,
                          );
                        }}
                        className="space-y-6 w-full"
                      >
                        {/* Managed OAuth has no user-supplied credentials, and
                          connect-without-credentials likewise has no auth —
                          skip the credentials step in both cases. A managed
                          credential's fields live in the picker above. */}
                        {!hasNoAuth &&
                          !isManagedOAuth &&
                          !usesManagedCredential &&
                          !wizardData.connectWithoutCredentials && (
                            <>
                              {selectedOAuthFlow ? (
                                <OAuthClientCredentialsStep
                                  integration={integration}
                                  authMethodId={wizardData.authMethodId!}
                                  control={step2Form.control}
                                />
                              ) : (
                                <CustomCredentialsStep
                                  integration={integration}
                                  authMethodId={wizardData.authMethodId!}
                                  control={step2Form.control}
                                />
                              )}
                            </>
                          )}
                        {/* Show config_map fields if integration has config_map.
                            It configures the connection, not a credential being created. */}
                        {integrationHasConfigMap &&
                          (!isCreatingDraftCredential ||
                            (asksConfigBeforeSignIn &&
                              isOAuthAuthorizationCode)) && (
                            <ConfigMapStep
                              integration={integration}
                              control={step2Form.control}
                              fieldPrefix="configMap"
                              showHeader={!hasNoAuth && !isManagedOAuth}
                            />
                          )}
                        {/* Show checkbox to add production credentials later when createSameConnection is false */}
                        {!wizardData.createSameConnection &&
                          !requireProductionCredentials &&
                          !hasNoAuth &&
                          !isCreatingDraftCredential && (
                            <Flex align="center" gap={2} className="pt-2">
                              <Checkbox
                                id="add-production-later"
                                checked={wizardData.addProductionLater || false}
                                onCheckedChange={checked => {
                                  setWizardData(prev => ({
                                    ...prev,
                                    addProductionLater: checked === true,
                                  }));
                                }}
                              />
                              <Label
                                htmlFor="add-production-later"
                                className="text-sm font-normal cursor-pointer"
                              >
                                {tIntegrations(
                                  'integrations.addConnection.addProductionLater',
                                )}
                              </Label>
                            </Flex>
                          )}
                        {/* Show OAuth button for authorization code flow */}
                        {isOAuthAuthorizationCode &&
                          !usesManagedCredential &&
                          selectedAuth &&
                          onOAuthClick && (
                            <OAuthSignInButton
                              integration={integration}
                              authentication={selectedAuth}
                              disabled={!step2Form.formState.isValid}
                              isLoading={isOAuthLoading}
                              onClick={handleOAuthButtonClick}
                            />
                          )}
                      </form>
                    </Form>
                  </>
                )}

              {currentStep === 3 &&
                currentStep !== remoteConnectionStepNumber &&
                wizardData.authMethodId && (
                  <>
                    {usesManagedCredential && (
                      <ManagedCredentialStep
                        integration={integration}
                        authMethodId={wizardData.authMethodId}
                        idPrefix="published-credential"
                        credentials={compatibleCredentials}
                        isLoading={isLoadingCompatibleCredentials}
                        isError={isCompatibleCredentialsError}
                        selection={publishedCredential.selection}
                        onSelectionChange={publishedCredential.setSelection}
                        form={publishedCredential.form}
                        defaultDisplayName={publishedCredentialName}
                        canChooseScope={canChooseCredentialScope}
                        oauthArguments={oauthArgumentDescriptors}
                        oauthProviderName={oauthProviderName}
                        managedCredentialName={
                          sharedEntry?.kind === 'secret'
                            ? sharedEntry.secret.display_name
                            : undefined
                        }
                      />
                    )}
                    <Form {...step3Form}>
                      <form
                        onSubmit={event => {
                          if (usesManagedCredential) {
                            event.preventDefault();
                            submitManagedCredentialStep(
                              EnvironmentType.Published,
                            );
                            return;
                          }
                          void step3Form.handleSubmit(
                            submitPublishedCredentials,
                          )(event);
                        }}
                        className="space-y-6 w-full"
                      >
                        {usesManagedCredential ? null : selectedOAuthFlow ? (
                          <OAuthClientCredentialsStep
                            integration={integration}
                            authMethodId={wizardData.authMethodId}
                            control={step3Form.control}
                          />
                        ) : (
                          <CustomCredentialsStep
                            integration={integration}
                            authMethodId={wizardData.authMethodId}
                            control={step3Form.control}
                          />
                        )}
                        {showReuseAlert && !usesManagedCredential && (
                          <ReuseTestConnectionAlert
                            onReuse={handleReuseTestConnection}
                            disabled={isLoading}
                          />
                        )}
                        {/* Show config_map fields if integration has config_map.
                            It configures the connection, not a credential being created. */}
                        {integrationHasConfigMap &&
                          !isCreatingPublishedCredential && (
                            <ConfigMapStep
                              integration={integration}
                              control={step3Form.control}
                              fieldPrefix="configMap"
                              showHeader
                            />
                          )}
                      </form>
                    </Form>
                  </>
                )}

              {/* Remote connection step - shown as the last step when remote connections exist */}
              {currentStep === remoteConnectionStepNumber && (
                <RemoteConnectionStep
                  remoteConnections={remoteConnections}
                  connectionType={
                    wizardData.connectionType || ConnectionType.Public
                  }
                  selectedRemoteConnectionId={wizardData.remoteConnectionId}
                  onConnectionTypeChange={handleConnectionTypeChange}
                  onRemoteConnectionSelect={handleRemoteConnectionSelect}
                />
              )}
            </Flex>
          )}
        </div>
      </>
    );
  };

  const renderFooter = () => {
    return (
      <DialogFooter className="shrink-0 w-full mt-auto sm:justify-between">
        <div>
          <Button variant="outline" onClick={handleCancel}>
            {tCommon('common.action.cancel')}
          </Button>
        </div>

        <Flex gap={2} align="center">
          {canGoBack && (
            <Button
              data-testid="connection-dialog-back"
              variant="outline"
              onClick={handleBack}
              disabled={isLoading}
            >
              <Icon type="ArrowLeft" size="sm" />
              {tCommon('common.action.back')}
            </Button>
          )}

          {/* Hide Next/Connect button for OAuth authorization code on step 2 (OAuth button is shown instead) */}
          {!hidesFooterForClassicOAuth && !hidesFooterForInlineSignIn && (
            <>
              {(isShowingConnectionMethodStep || canGoNext) && (
                <Button
                  data-testid="connection-dialog-continue"
                  onClick={handleNext}
                  disabled={
                    isLoading ||
                    hasInvalidCredentialKeys ||
                    isManagedCredentialMissing ||
                    showReuseAlert ||
                    (currentStep === remoteConnectionStepNumber &&
                      wizardData.connectionType === ConnectionType.Remote &&
                      !wizardData.remoteConnectionId)
                  }
                >
                  {getButtonText()}
                </Button>
              )}

              {!isShowingConnectionMethodStep && currentStep === totalSteps && (
                <Button
                  data-testid="connection-dialog-connect"
                  onClick={handleNext}
                  disabled={
                    isLoading ||
                    hasInvalidCredentialKeys ||
                    isManagedCredentialMissing ||
                    showReuseAlert ||
                    (currentStep === remoteConnectionStepNumber &&
                      wizardData.connectionType === ConnectionType.Remote &&
                      !wizardData.remoteConnectionId)
                  }
                  isLoading={isLoading}
                >
                  {getButtonText()}
                </Button>
              )}
            </>
          )}
        </Flex>
      </DialogFooter>
    );
  };

  const dialogClassName = `${
    openSidePanel ? 'sm:max-w-[1180px]' : 'sm:max-w-xl'
  } h-[calc(100vh-5rem)] lg:h-[calc(100vh-10rem)] [@media(max-height:800px)]:lg:h-[calc(100vh-5rem)] [@media(max-height:800px)]:lg:max-h-[calc(100vh-5rem)] flex flex-col overflow-hidden transition-[max-width] duration-300 focus:outline-none focus-visible:outline-none`;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="add-connection-dialog"
        className={dialogClassName}
        onOpenAutoFocus={handleDialogOpenAutoFocus}
        aria-describedby={undefined}
      >
        <div className="flex flex-1 min-h-0 sm:min-h-[280px] -mx-6 overflow-hidden relative">
          <Flex
            direction="column"
            align="stretch"
            gap={4}
            className={`flex-1 min-w-0 min-h-0 overflow-hidden px-6 ${openSidePanel ? 'mr-[612px]' : ''} transition-[margin] duration-300`}
          >
            {/* pr-8 keeps the assistant button clear of the dialog's absolute close X. */}
            <Flex
              align="start"
              justify="between"
              gap={3}
              className="w-full shrink-0 pr-8"
            >
              <DialogHeader className="min-w-0">
                <DialogTitle>
                  {tIntegrations('integrations.addConnection.title', {
                    integrationName:
                      integration.display_name || integration.name,
                  })}
                </DialogTitle>
                {panelConnection && !isCheckingConnection && (
                  <Flex
                    align="center"
                    gap={2}
                    className="flex-wrap"
                    data-testid="add-connection-header-meta"
                  >
                    {shouldShowHealthDetails(panelConnection.status) ? (
                      <ConnectionDiagnosticsPopover
                        connection={panelConnection}
                        displayName={
                          integration.display_name || integration.name
                        }
                        showAnalysis={false}
                      >
                        {panelStatusTag}
                      </ConnectionDiagnosticsPopover>
                    ) : (
                      panelStatusTag
                    )}
                  </Flex>
                )}
              </DialogHeader>
              {isAssistantEnabled &&
                !isAssistantOpen &&
                !isCheckingConnection && (
                  <IntegrationAssistantBanner
                    compact
                    variant={diagnosedConnectionId ? 'diagnosis' : 'connect'}
                    onOpenAssistant={() => setOpenSidePanel('assistant')}
                  />
                )}
            </Flex>
            {isLoadingIntegrationConnections ||
            isLoadingRemoteConnections ||
            sharedAuth.isLoading ? (
              <Flex justify="center" align="center" className="min-h-30">
                <Spinner size="lg" />
              </Flex>
            ) : (
              <div className="relative flex-1 flex flex-col min-h-0">
                {isCheckingConnection && (
                  <Flex
                    direction="column"
                    className="mb-4 w-full rounded-md border p-4"
                  >
                    <ConnectionDiagnosticsDetail connection={panelConnection} />
                  </Flex>
                )}
                <div
                  className={`flex-1 flex flex-col min-h-0 ${isLoading ? 'opacity-25 pointer-events-none' : ''}`}
                >
                  {renderContent()}
                </div>
                {isLoading &&
                  !panelConnection &&
                  (integration.discover_capable ? (
                    <Flex
                      direction="column"
                      gap={2}
                      align="center"
                      justify="center"
                      className="absolute inset-0"
                    >
                      <Flex gap={2} align="center" justify="center">
                        <Spinner />
                        <Text weight="medium" className="text-center">
                          {tIntegrations(
                            'integrations.addConnection.connectingTitle',
                            {
                              integrationName:
                                integration.display_name || integration.name,
                            },
                          )}
                        </Text>
                      </Flex>
                      <Text color="muted" level="small" className="text-center">
                        {tIntegrations(
                          isConnectionTakingTooLong
                            ? 'integrations.addConnection.connectingTooLong'
                            : 'integrations.addConnection.connectingSubtitle',
                        )}
                      </Text>
                    </Flex>
                  ) : (
                    <Flex
                      justify="center"
                      align="center"
                      className="absolute inset-0"
                    >
                      <Spinner size="lg" />
                    </Flex>
                  ))}
              </div>
            )}

            {/* Footer (including Cancel) is hidden during the initial connection fetch
                so the Continue/Connect buttons never fire against an empty dataset.
                ESC and click-outside still close the dialog during this brief load. */}
            {!(
              isLoadingIntegrationConnections ||
              isLoadingRemoteConnections ||
              sharedAuth.isLoading
            ) && renderFooter()}
          </Flex>

          {isAssistantEnabled && (
            <IntegrationAssistantPanel
              integrationName={integration.name || ''}
              integrationDisplayName={
                integration.display_name || integration.name || ''
              }
              integrationVersion={integration.version || ''}
              organizationId={organizationId}
              workspaceId={workspaceId}
              // The gateway routes by authentication OR connection, never both.
              selectedAuthId={
                !diagnosedConnectionId && currentStep > 1
                  ? getAuthMethodId(selectedAuth)
                  : undefined
              }
              connectionId={diagnosedConnectionId}
              diagnosis={completedDiagnosis}
              isDiagnosisLoading={diagnosisInsight.isLoading}
              disabled={isLoading}
              isOpen={isAssistantOpen}
              onClose={() => setOpenSidePanel(null)}
            />
          )}
        </div>
        <EmptyCredentialValuesConfirmDialog
          {...emptyCredentialValuesDialogProps}
        />
      </DialogContent>
    </Dialog>
  );
}
